import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { TrendingUp, Clock, Users, Zap, Sparkles, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function BoostPostDialog({ post, open, onClose, onSuccess }) {
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [boosting, setBoosting] = useState(false);

  useEffect(() => {
    if (open) {
      loadPackages();
    }
  }, [open]);

  const loadPackages = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/social/boost-packages`, {
        credentials: 'include'
      });

      if (res.ok) {
        const data = await res.json();
        setPackages(data.packages || []);
        // Pre-select medium package
        if (data.packages && data.packages.length >= 2) {
          setSelectedPackage(data.packages[1]);
        }
      }
    } catch (error) {
      toast.error('Failed to load boost packages');
    } finally {
      setLoading(false);
    }
  };

  const handleBoost = async () => {
    if (!selectedPackage) {
      toast.error('Please select a boost package');
      return;
    }

    try {
      setBoosting(true);
      const res = await fetch(`${API_URL}/api/social/posts/${post.id}/boost`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          post_id: post.id,
          package_size: selectedPackage.id
        })
      });

      const data = await res.json();

      if (res.ok) {
        toast.success(`🚀 Post boosted for ${selectedPackage.duration_days} days!`);
        onSuccess();
        onClose();
      } else {
        toast.error(data.detail || 'Failed to boost post');
      }
    } catch (error) {
      toast.error('Failed to boost post');
    } finally {
      setBoosting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-500" />
            Boost Your Post
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Post Preview */}
          <Card className="bg-gray-50 dark:bg-gray-900">
            <CardContent className="pt-4">
              <p className="text-sm line-clamp-3">{post.content}</p>
            </CardContent>
          </Card>

          {/* Info Banner */}
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-start gap-2">
              <Sparkles className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold text-blue-900 dark:text-blue-100 text-sm mb-1">
                  Get More Visibility
                </h4>
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  Boosted posts appear at the top of feeds and reach more people. Perfect for important announcements, promotions, or content you want to amplify.
                </p>
              </div>
            </div>
          </div>

          {/* Packages */}
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading packages...</div>
          ) : (
            <div className="space-y-3">
              <h3 className="font-semibold">Choose a boost package</h3>
              
              {packages.map((pkg) => (
                <Card
                  key={pkg.id}
                  className={`cursor-pointer transition-all ${
                    selectedPackage?.id === pkg.id
                      ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'hover:border-blue-300'
                  }`}
                  onClick={() => setSelectedPackage(pkg)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-semibold text-lg">{pkg.name}</h4>
                          {pkg.id === 'medium' && (
                            <Badge variant="secondary" className="text-xs">
                              Most Popular
                            </Badge>
                          )}
                          {selectedPackage?.id === pkg.id && (
                            <CheckCircle className="w-5 h-5 text-blue-600" />
                          )}
                        </div>
                        
                        <div className="grid grid-cols-3 gap-3 mb-2">
                          <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
                            <Clock className="w-4 h-4" />
                            <span>{pkg.duration_days} {pkg.duration_days === 1 ? 'day' : 'days'}</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
                            <Users className="w-4 h-4" />
                            <span>{pkg.estimated_reach}</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
                            <TrendingUp className="w-4 h-4" />
                            <span>Priority</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="text-2xl font-bold text-blue-600">
                          {pkg.price_mtr}
                        </div>
                        <div className="text-xs text-gray-500">MTR</div>
                        {pkg.price_zar !== pkg.price_mtr && (
                          <div className="text-xs text-gray-400 mt-1">
                            ≈ R{pkg.price_zar.toFixed(2)}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Benefits */}
          <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
            <p className="font-semibold text-gray-900 dark:text-gray-100">Boost Benefits:</p>
            <ul className="space-y-1.5 ml-4">
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <span>Priority placement in For You and Trending feeds</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <span>Increased visibility to users in your area</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <span>Detailed analytics on reach and engagement</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <span>Cancel anytime with prorated refund</span>
              </li>
            </ul>
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={onClose} disabled={boosting}>
            Cancel
          </Button>
          <Button
            onClick={handleBoost}
            disabled={!selectedPackage || boosting}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            {boosting ? (
              'Boosting...'
            ) : selectedPackage ? (
              <>
                <Zap className="w-4 h-4 mr-2" />
                Boost for {selectedPackage.price_mtr} MTR
              </>
            ) : (
              'Select Package'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
